/**
 * 입력이 있는 모달은 바깥(오버레이 · 배경)을 눌러도 닫히지 않는다 (2026-09-30, 사용자
 * 「esc 는 몰라도 아무튼 입력이 있는 modal 들은 overlay click 시에 꺼지는건 아니지」).
 *
 * 모달마다 바깥 클릭으로 닫는 처리가 따로 붙어 있다(모듈 핸들러 · 인라인 onclick, 40곳 가까이).
 * 그것들을 하나씩 고치는 대신 문서의 capture 단계에서 한 번 막는다: 누른 요소가 오버레이 자체이고
 * 그 모달에 고칠 수 있는 칸(입력 · 선택 · 글상자 · contenteditable)이 있으면 이벤트를 더 보내지
 * 않는다 — 그러면 오버레이의 click/mousedown 핸들러도, 문서에 위임한 핸들러도, 인라인 onclick 도
 * 받지 못한다. stopImmediatePropagation 이 아니라 stopPropagation 이라 문서에 붙은 다른 capture
 * 리스너(공용 팝오버 등록부 등 — 모달 안에 열린 날짜 고르기 같은 것을 닫는다)는 그대로 돈다.
 * Esc · × · 취소는 그대로 닫는다. 칸이 없는 모달(확인 창 · 파일 보기 · 미리보기)은 예전처럼
 * 바깥 클릭으로 닫힌다. 막았을 때는 대화 상자를 살짝 튕겨 「바깥은 닫지 않는다」를 알린다.
 *
 * 오버레이로 치는 것: 공용 `.modal-overlay`(대화 상자를 품은 겉 요소) · 따로 둔 배경 요소
 * `[data-modal-backdrop]`(옛 관리 화면의 인라인 onclick 배경) · 근태 결재 · 결재 문서 모달의
 * 배경. 옆 패널(일정 · 업무 요청 · 영업 기회 · 인사 패널)과 폰의 바텀 시트는 여기서 다루지 않는다
 * — 그것들은 저장하지 않은 입력이 있으면 스스로 묻는다.
 */

const BACKDROP = '.modal-overlay, [data-modal-backdrop], .att-appr-backdrop, #attAdmBackdrop, .apv-modal-backdrop';

const FIELD = [
    'input:not([type="hidden"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="image"]):not([readonly]):not([disabled])',
    'select:not([disabled])',
    'textarea:not([readonly]):not([disabled])',
    '[contenteditable=""]',
    '[contenteditable="true"]',
].join(', ');

// The element the backdrop belongs to: a `.modal-overlay` wraps its dialog; a separate
// backdrop element sits beside the dialog inside the modal's root.
function modalRootOf(backdrop) {
    return backdrop.matches('.modal-overlay') ? backdrop : backdrop.parentElement;
}

function dialogOf(backdrop, root) {
    if (backdrop.matches('.modal-overlay')) {
        return root.querySelector(':scope > .modal, :scope > [role="dialog"], :scope > div') || root;
    }
    return backdrop.nextElementSibling || root;
}

/** holdsBackdropClick reports whether a press on `target` must not reach the modal's close handlers. */
export function holdsBackdropClick(target) {
    if (!(target instanceof Element) || !target.matches(BACKDROP)) return false;
    const root = modalRootOf(target);
    return !!(root && root.querySelector(FIELD));
}

const nudging = new WeakSet();
function nudge(target) {
    const dialog = dialogOf(target, modalRootOf(target));
    if (!dialog || nudging.has(dialog)) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    nudging.add(dialog);
    dialog.classList.add('modal-nudge');
    setTimeout(() => {
        dialog.classList.remove('modal-nudge');
        nudging.delete(dialog);
    }, 220);
}

function guard(event) {
    if (!holdsBackdropClick(event.target)) return;
    event.stopPropagation();
    if (event.type === 'click') nudge(event.target);
}

let wired = false;
export function setupModalBackdropGuard() {
    if (wired) return;
    wired = true;
    ['pointerdown', 'mousedown', 'click'].forEach((type) => document.addEventListener(type, guard, true));
}
